import apiClient from './apiClient';

/**
 * Admin kullanıcı girişi
 * @param {string} email - Kullanıcı e-posta adresi veya kullanıcı adı
 * @param {string} password - Şifre (plain text)
 */
export const signIn = async (email, password) => {
  try {
    // Email'i temizle
    const cleanEmail = email.trim().toLowerCase();
    
    if (!cleanEmail || cleanEmail.length < 3) {
      return {
        success: false,
        error: 'Geçerli bir e-posta adresi veya kullanıcı adı giriniz'
      };
    }

    if (!password || password.length < 1) {
      return {
        success: false,
        error: 'Şifre gereklidir'
      };
    }

    // Backend API'ye giriş isteği gönder
    const response = await apiClient.post('/auth/login', {
      email: cleanEmail,
      password: password.trim()
    });

    console.log('Login response:', response);

    if (!response.success) {
      return {
        success: false,
        error: response.error || 'Giriş başarısız'
      };
    }

    const { user, token } = response.data;

    // Token'ı local storage'a kaydet
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));

    return {
      success: true,
      user: {
        id: user.id,
        email: user.email,
        fullName: user.fullName,
        role: user.role,
        access_token: token
      },
      session: {
        access_token: token,
        user: {
          id: user.id,
          email: user.email,
          role: user.role
        }
      }
    };
  } catch (error) {
    console.error('❌ Login hatası:', error);
    // Hata mesajını farklı kaynaklardan almayı dene
    const errorMessage = error?.error || 
                         error?.message || 
                         'Giriş yapılırken bir hata oluştu';
    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Çıkış yap
 */
export const signOut = async () => {
  try {
    // Backend'e çıkış bildir (opsiyonel)
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Çıkış hatası görmezden gel
      console.warn('Logout warning:', error?.message || error);
    }

    // Local storage'ı temizle
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    return { success: true };
  } catch (error) {
    console.error('❌ Logout hatası:', error);
    return { success: false, error: error?.message || 'Çıkış hatası' };
  }
};

/**
 * Mevcut session'ı kontrol et
 */
export const getSession = async () => {
  try {
    // Local storage'dan session bilgisini al
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (!token || !userStr) {
      return { success: true, session: null, user: null };
    }

    try {
      const user = JSON.parse(userStr);

      // Backend'de token geçerliliğini kontrol et
      try {
        const response = await apiClient.get('/auth/session');
        
        if (response.success) {
          const sessionUser = response.data;
          return {
            success: true,
            session: {
              access_token: token,
              user: sessionUser
            },
            user: sessionUser
          };
        }
      } catch (error) {
        // Token geçersiz veya süresi dolmuş
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return { success: true, session: null, user: null };
      }

      return {
        success: true,
        session: {
          access_token: token,
          user: user
        },
        user: user
      };
    } catch (e) {
      // Token veya user geçersiz
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return { success: true, session: null, user: null };
    }
  } catch (error) {
    console.error('❌ Session kontrol hatası:', error);
    return { success: false, session: null, user: null, error: error?.message || 'Session hatası' };
  }
};

/**
 * Müşteri girişi
 * @param {string} email - Müşteri e-posta adresi
 * @param {string} password - Şifre (plain text)
 */
export const customerSignIn = async (email, password) => {
  try {
    // Email'i temizle
    const cleanEmail = email.trim().toLowerCase();
    
    if (!cleanEmail || !cleanEmail.includes('@')) {
      return {
        success: false,
        error: 'Geçerli bir e-posta adresi giriniz'
      };
    }

    if (!password || password.length < 1) {
      return {
        success: false,
        error: 'Şifre gereklidir'
      };
    }

    // Backend API'ye müşteri girişi isteği gönder
    const response = await apiClient.post('/auth/customer-login', {
      email: cleanEmail,
      password: password.trim()
    });

    if (!response.success) {
      return {
        success: false,
        error: response.error || 'Giriş başarısız'
      };
    }

    const { customer, token } = response.data;

    // Token'ı local storage'a kaydet
    localStorage.setItem('customer_token', token);
    localStorage.setItem('customer', JSON.stringify(customer));

    return {
      success: true,
      customer: {
        id: customer.id,
        name: customer.name,
        email: customer.email,
        access_token: token
      },
      session: {
        access_token: token,
        customer: {
          id: customer.id,
          name: customer.name,
          email: customer.email
        }
      }
    };
  } catch (error) {
    console.error('❌ Customer Login hatası:', error);
    const errorMessage = error?.error || 
                         error?.message || 
                         'Giriş yapılırken bir hata oluştu';
    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Müşteri session'ını kontrol et
 */
export const getCustomerSession = async () => {
  try {
    // Local storage'dan session bilgisini al
    const token = localStorage.getItem('customer_token');
    const customerStr = localStorage.getItem('customer');

    if (!token || !customerStr) {
      return { success: true, session: null, customer: null };
    }

    try {
      const customer = JSON.parse(customerStr);

      // Basit geçerlilik kontrolü
      if (!customer.id || !customer.email) {
        localStorage.removeItem('customer_token');
        localStorage.removeItem('customer');
        return { success: true, session: null, customer: null };
      }

      return {
        success: true,
        session: {
          access_token: token,
          customer: customer
        },
        customer: customer
      };
    } catch (e) {
      // Token veya customer geçersiz
      localStorage.removeItem('customer_token');
      localStorage.removeItem('customer');
      return { success: true, session: null, customer: null };
    }
  } catch (error) {
    console.error('❌ Customer session kontrol hatası:', error);
    return { success: false, session: null, customer: null, error: error?.message || 'Session hatası' };
  }
};

/**
 * Müşteri çıkışı
 */
export const customerSignOut = async () => {
  try {
    // Local storage'ı temizle
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer');

    return { success: true };
  } catch (error) {
    console.error('❌ Customer logout hatası:', error);
    return { success: false, error: error?.message || 'Çıkış hatası' };
  }
};

/**
 * Auth state değişikliklerini dinle
 */
export const onAuthStateChange = (callback) => {
  // Local storage değişikliklerini dinle
  const handleStorageChange = (e) => {
    if (e.key === 'token' || e.key === 'user') {
      getSession().then(result => {
        callback(result.user ? 'SIGNED_IN' : 'SIGNED_OUT', result.session);
      });
    }
  };
  
  window.addEventListener('storage', handleStorageChange);
  
  // İlk kontrol
  getSession().then(result => {
    callback(result.user ? 'SIGNED_IN' : 'SIGNED_OUT', result.session);
  });
  
  // Cleanup function döndür
  return () => {
    window.removeEventListener('storage', handleStorageChange);
  };
};
